import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, message, Tag, Space, Input, Popconfirm } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import request from '../utils/request';
import { useAuth } from '../contexts/AuthContext';

const { TextArea } = Input;

interface Withdrawal {
  id: number;
  user_address: string;
  amount: number;
  status: number;
  created_at: string;
  updated_at: string;
  remark: string;
  user_id: number;
}

const WithdrawalList: React.FC = () => {
  const { user } = useAuth();
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState<number | undefined>(undefined);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);
  const [remark, setRemark] = useState('');
  const [reviewStatus, setReviewStatus] = useState<1 | 2>(1);
  const navigate = useNavigate();

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const response = await request.get(`/withdrawals`, {
        params: {
          page: currentPage,
          pageSize,
          status,
          uid: user?.id,
          level_id: user?.level_id
        }
      });
      if (response.code === 200) {
        setWithdrawals(response.data.list);
        setTotal(response.data.total);
      }
    } catch (error) {
      message.error('获取提现列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, [currentPage, pageSize, status]);

  const handleReview = (withdrawal: Withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setReviewModalVisible(true);
  };

  const handleReviewSubmit = async () => {
    if (!selectedWithdrawal) return;

    try {
      await request.post(`/withdrawals/${selectedWithdrawal.id}/review`, {
        status: reviewStatus
      });
      message.success('审核成功');
      setReviewModalVisible(false);
      fetchWithdrawals();
    } catch (error) {
      message.error('审核失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await request.delete(`/withdrawals/${id}`);
      message.success('删除成功');
      fetchWithdrawals();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '用户ID',
      dataIndex: 'user_id',
      key: 'user_id',
    },
    {
      title: '用户地址',
      dataIndex: 'user_address',
      key: 'user_address',
    },
    {
      title: '提现金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `${amount} USDT`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => {
        const statusMap = {
          0: { text: '待审核', color: 'orange' },
          1: { text: '已通过', color: 'green' },
          2: { text: '已拒绝', color: 'red' },
        };
        return <Tag color={statusMap[status as keyof typeof statusMap].color}>
          {statusMap[status as keyof typeof statusMap].text}
        </Tag>;
      },
    },
    {
      title: '申请时间',
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Withdrawal) => (
        <Space size="middle">
          {record.status === 0 && (
            <Button type="link" onClick={() => handleReview(record)}>
              审核
            </Button>
          )}
          {record.status !== 0 && (
            <Popconfirm
              title="确定要删除这条提现记录吗？"
              description="删除后将无法恢复"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" danger>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button onClick={() => setStatus(undefined)}>全部</Button>
          <Button onClick={() => setStatus(0)}>待审核</Button>
          <Button onClick={() => setStatus(1)}>已通过</Button>
          <Button onClick={() => setStatus(2)}>已拒绝</Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={withdrawals}
        rowKey="id"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          onChange: (page, pageSize) => {
            setCurrentPage(page);
            setPageSize(pageSize);
          },
        }}
      />

      <Modal
        title="审核提现申请"
        open={reviewModalVisible}
        onOk={handleReviewSubmit}
        onCancel={() => setReviewModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <div>用户地址: {selectedWithdrawal?.user_address}</div>
          <div>提现金额: {selectedWithdrawal?.amount} USDT</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div>审核结果:</div>
          <Space>
            <Button
              type={reviewStatus === 1 ? 'primary' : 'default'}
              onClick={() => setReviewStatus(1)}
            >
              通过
            </Button>
            <Button
              type={reviewStatus === 2 ? 'primary' : 'default'}
              onClick={() => setReviewStatus(2)}
            >
              拒绝
            </Button>
          </Space>
        </div>
        <div>
          <div>备注:</div>
          <TextArea
            rows={4}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="请输入审核备注"
          />
        </div>
      </Modal>
    </div>
  );
};

export default WithdrawalList; 